import { tariffsApi } from '../client';
import type { TariffCurrency } from '../types';

export interface CurrencyOption {
  value: string;
  text: string;
}

/** GET /currencies/ — paginated currency catalog. */
export async function listCurrencies(params?: {
  offset?: number;
  limit?: number;
  status?: string | null;
}): Promise<TariffCurrency[]> {
  const { data, error } = await tariffsApi.GET('/currencies/', {
    params: {
      query: {
        offset: params?.offset ?? 0,
        limit: params?.limit ?? 500,
        'sort.name': 'short_name',
        'sort.direction': 'asc',
        status: params?.status ?? null,
      },
    },
  });
  if (error) throw error;
  return data?.items ?? [];
}

/** Short names for tariff price maps (`RUB`, `USD`, …). */
export async function fetchCurrencies(): Promise<string[]> {
  const items = await listCurrencies();
  const codes = [...new Set(items.map((c) => c.short_name.trim().toUpperCase()).filter(Boolean))];
  if (!codes.length) {
    throw new Error('Список валют пуст');
  }
  return codes.sort((a, b) => a.localeCompare(b));
}

export async function fetchCurrencyOptions(): Promise<CurrencyOption[]> {
  const items = await listCurrencies();
  return items
    .map((c) => ({
      value: c.short_name.trim().toUpperCase(),
      text: `${c.short_name} — ${c.name}`,
    }))
    .sort((a, b) => a.value.localeCompare(b.value));
}

export async function convertCurrencyAmount(
  amount: number,
  baseCurrency: string,
  targetCurrency: string,
): Promise<number> {
  if (baseCurrency === targetCurrency) return amount;
  const { data, error } = await tariffsApi.POST('/currencies/convert/', {
    body: {
      amount,
      base_currency: baseCurrency,
      target_currency: targetCurrency,
    },
  });
  if (error) throw error;
  if (data?.amount == null) throw new Error('Сервер не вернул сумму конвертации');
  return data.amount;
}

/** Exchange rate: 1 unit of `baseCurrency` → `targetCurrency`. */
export async function getExchangeRate(
  baseCurrency: string,
  targetCurrency: string,
): Promise<number> {
  return convertCurrencyAmount(1, baseCurrency, targetCurrency);
}
